import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import {
  OnTopCard,
  OnTopCardComponentThemeProps,
  componentConfigThemeKey as onTopCardConfigThemeKey,
} from '../../partials/cards/onTopCard/OnTopCard';
import { useTheme } from '../../../theming/useTheme';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { ThemeContext } from '../../../theming/ThemeProviderWithFonts';
import { useThemeOverride } from '../../../theming/useThemeOverride';
import { CardComponentDataProps } from '../../partials/cards/cards.utils';
import { baseUnit } from '../../../theming/baseUnitDefinition';
import { createImageHeightCalculationMethods, createImageWidths } from '../../partials/image/image.utils';

const componentConfigThemeKey = 'oneTwoOnTopFullWidth';

interface OneTwoOnTopFullWidthThemeProps {
  largeVerticalGap: number;
  largeHorizontalGap: number;
  mediumVerticalGap: number;
  mediumHorizontalGap: number;
  smallHorizontalGap: number;
}

interface OneTwoOnTopFullWidthDataProps {
  cards: Array<CardComponentDataProps>;
}

const getStyles = (theme: OneTwoOnTopFullWidthThemeProps) => {
  const { largeHorizontalGap, largeVerticalGap, mediumHorizontalGap, mediumVerticalGap, smallHorizontalGap } = theme;
  return StyleSheet.create({
    container: {
      marginTop: `${baseUnit * 0.25}px`,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateRows: '1fr auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'block',
      },
    },
    lastCardsContainer: {
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'block',
      },
    },
    secondAndThirdCards: {
      [MEDIA_BREAKPOINTS.large]: {
        height: '50%',
        boxSizing: 'border-box',
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginTop: `${smallHorizontalGap * baseUnit}px`,
      },
    },
    secondChild: {
      [MEDIA_BREAKPOINTS.large]: {
        paddingBottom: `${(largeHorizontalGap * baseUnit) / 2}px`,
      },
    },
    thirdChild: {
      [MEDIA_BREAKPOINTS.large]: {
        paddingTop: `${(largeHorizontalGap * baseUnit) / 2}px`,
      },
    },
  });
};

const secondAndThirdImageHeightCalculationMethods = createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT');

const addImageHeightCalculationMethodsProp = (cardProps: CardComponentDataProps) => ({
  ...cardProps,
  imageHeightCalculationMethods: secondAndThirdImageHeightCalculationMethods,
});

const imageWidthsMainCard = createImageWidths(450, 750, 750);
const imageWidthsMinorCards = createImageWidths(450, 450, 450);

export const OneTwoOnTopFullWidth: React.FunctionComponent<OneTwoOnTopFullWidthDataProps> = props => {
  const themeProps = useTheme<OneTwoOnTopFullWidthThemeProps>(componentConfigThemeKey);
  const styles = getStyles(themeProps);
  const { cards } = props;
  const topCard = cards[0];
  const secondCard = addImageHeightCalculationMethodsProp(cards[1]);
  const thirdCard = addImageHeightCalculationMethodsProp(cards[2]);
  const themeContextFirstCard = useThemeOverride<OnTopCardComponentThemeProps>({
    titleFontSizeMedium: 'huge',
    titleFontSizeLarge: 'huge',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.5,
    horizontalGapLarge: 0.5,
    verticalGapSmall: 0.5,
    verticalGapMedium: 0.5,
    verticalGapLarge: 0.75,
    titleSemanticTag: 'h2',
  }, onTopCardConfigThemeKey);
  const themeContextSecondAndThirdCards = useThemeOverride<OnTopCardComponentThemeProps>({
    titleFontSizeMedium: 'bigger',
    titleFontSizeLarge: 'bigger',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.25,
    horizontalGapLarge: 0.25,
    verticalGapSmall: 0.5,
    verticalGapMedium: 0.5,
    verticalGapLarge: 0.5,
  }, onTopCardConfigThemeKey);
  return (
    <ErrorBoundary>
      <div className={css(styles.container)}>
        <ThemeContext.Provider value={themeContextFirstCard}>
          <OnTopCard {...topCard} imageWidths={imageWidthsMainCard} />
        </ThemeContext.Provider>
        <div className={css(styles.lastCardsContainer)}>
          <ThemeContext.Provider value={themeContextSecondAndThirdCards}>
            <div className={css(styles.secondAndThirdCards, styles.secondChild)}>
              <OnTopCard {...secondCard} imageWidths={imageWidthsMinorCards} />
            </div>
            <div className={css(styles.secondAndThirdCards, styles.thirdChild)}>
              <OnTopCard {...thirdCard} imageWidths={imageWidthsMinorCards} />
            </div>
          </ThemeContext.Provider>
        </div>
      </div>
    </ErrorBoundary>
  );
};
